import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertColor,
  AlertTitle,
  Box,
  IconButton,
  Snackbar,
  SnackbarProps,
  styled,
  Typography
} from "@mui/material";

import * as React from "react";
import { FC } from "react";

import { useTranslation } from "react-i18next";

import { ToastMessage } from "./useToast";

export type ToastStyle = Omit<
  SnackbarProps,
  "TransitionProps" | "onClose" | "open" | "children" | "message"
>;

export type ToastProps = {
  message: ToastMessage;
  onExited: () => void;
  onExiting?: () => void;
  onUserDismissed?: () => void;
  duplicateToastCount: number; // used to track how many times the same toast has been shown
} & ToastStyle;

const defaultDuration: { [Property in AlertColor]: number } = {
  error: 10000,
  warning: 10000,
  info: 3000,
  success: 3000
};

const FuseUnderlay = styled(Box, {
  shouldForwardProp: (propName: string) => !["toastSeverity"].includes(propName)
})<{ toastSeverity: AlertColor }>(({ toastSeverity, theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "2px",
  backgroundColor: theme.palette.toastFuseUnderlay[toastSeverity],
  borderBottomLeftRadius: "inherit",
  borderBottomRightRadius: "inherit",
  zIndex: 0
}));

const FuseBar = styled(Box, {
  shouldForwardProp: (propName: string) => !["duration"].includes(propName)
})<{ duration: number }>(({ duration, theme }) => ({
  boxSizing: "border-box",
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.toastFuseColor.main,
  height: "5px",
  zIndex: 1,
  borderBottomLeftRadius: "inherit",
  transformOrigin: "left",
  "@keyframes fuse": {
    "0%": {
      transform: "scaleX(1)",
      borderBottomRightRadius: "inherit"
    },
    "0.5%": {
      transform: "scaleX(1)",
      borderBottomRightRadius: "0px"
    },
    "100%": {
      transform: "scaleX(0)"
    }
  },
  animation: `fuse ${duration}ms linear forwards`
}));

// https://mui.com/material-ui/react-snackbar/#consecutive-snackbars
export const Toast: FC<ToastProps> = ({
  message,
  onExited,
  onExiting,
  onUserDismissed,
  duplicateToastCount,
  ...props
}) => {
  const duration = message.duration || defaultDuration[message.severity];

  const { t } = useTranslation();
  const [open, setOpen] = React.useState(true);

  const handleClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  React.useEffect(() => {
    if (duration <= 200 || !onExiting) return;
    const exitingAnimationTimer = setTimeout(() => {
      onExiting();
      //use a 200ms buffer to allow the exiting animation
      //to play before calling onExited
    }, duration - 200);

    return () => clearTimeout(exitingAnimationTimer);
  }, [duration, onExiting]);

  return (
    <Snackbar
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        left: "50%"
      }}
      key={message.key}
      open={open}
      onClose={handleClose}
      TransitionProps={{ onExited }}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      autoHideDuration={duration}
      {...props}
    >
      <Alert
        sx={{
          position: "relative",
          overflow: "hidden",
          pb: 0,
          borderBottom: `none`
        }}
        variant="filled"
        severity={message.severity}
        action={
          <IconButton
            aria-label="close"
            onClick={(event) => {
              onUserDismissed?.();
              handleClose(event);
            }}
            color="inherit"
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        }
      >
        {message.title && <AlertTitle>{message.title}</AlertTitle>}
        {message.description}
        {duplicateToastCount > 1 && (
          <Typography
            role="status"
            variant="caption"
            aria-label="Duplicate toast count"
            sx={{
              color: "inherit",
              mt: 0.5,
              fontStyle: "italic"
            }}
          >
            {t("toast count", {
              count: duplicateToastCount
            })}
          </Typography>
        )}
        <FuseBar
          role="timer"
          aria-label="Toast countdown indicator"
          duration={duration}
        />
        <FuseUnderlay
          role="presentation"
          aria-hidden="true"
          toastSeverity={message.severity}
        />
      </Alert>
    </Snackbar>
  );
};
